import { PlayerEnvironment } from './player_environment';
import LocationAdapter from '@javascripts/adapters/location_adapter';
import VideoHelpers from '@javascripts/utils/video_helpers.js';
import PlayerDvr from './player_dvr';
import isFeatureToggleOn from '@javascripts/utils/feature_toggle';
import { controlBarLocales } from '@javascripts/shared/locales'

export default class PlayerOptions {
  constructor({ sources, autoplay, data, uuid } = {}) {
    this._sources = sources;
    this._autoplay = autoplay || {};
    this._data = data;
    this._uuid = uuid;
  }

  sources() {
    return this._sources;
  }

  autoplay() {
    return this._autoplay;
  }

  data() {
    return this._data;
  }

  uuid() {
    return this._uuid;
  }

  swfUrl() {
    return '//vjs.zencdn.net/swf/5.4.1/video-js.swf';
  }

  ignoreClickVpVidioLogo() {
    return LocationAdapter.search().ignore_player_logo_click == 'true';
  }

  showSettingsButton() {
    const isDesktopSite = PlayerEnvironment.isDesktopSite();
    const isEmbed = this.data().embed() == "true";
    return (isDesktopSite || isEmbed);
  }

  showMobileSettingsButton() {
    const isMobileSite = PlayerEnvironment.isMobileSite();
    const isEmbed = this.data().embed() == "true";
    return isMobileSite && !isEmbed;
  }

  showSubtitleSwitcher() {
    const featureToggleIsOn = isFeatureToggleOn('enable_subtitle_from_master_playlist');
    const subtitleDataisExists = !VideoHelpers.isBlank(this.data().subtitles());
    return (featureToggleIsOn || subtitleDataisExists) && !this.showSettingsButton() && !this.showMobileSettingsButton();
  }

  value() {
    const browserName = PlayerEnvironment.browserName();
    const isChromeMobileButNotIos = browserName.toLowerCase().includes('chrome mobile') && browserName.toLowerCase() !== 'chrome mobile ios'
    const overrideNativeHls = browserName === 'Edge' || isChromeMobileButNotIos;

    const dvr_should_init = PlayerDvr.shouldInit({ data: this.data() });

    return {
      languages: controlBarLocales(),
      language: VideoHelpers.getLang(),
      inactivityTimeout: 5000,
      sources: this.sources(),
      aspectRatio: '16:9',
      autoplay: this.autoplay().value(),
      muted: browserName.includes('Safari') ? true : this.data().mute(),
      preload: PlayerEnvironment.isDesktopSite() ? 'none' : 'auto',
      poster: this.data().videoImageUrl(),
      reportTouchActivity: true,
      resizeManager: false,
      techOrder: ['html5'],
      html5:{
        nativeAudioTracks: !overrideNativeHls,
        nativeVideoTracks: !overrideNativeHls,
         vhs: {
          handleManifestRedirects: true,
          withCredentials: false,
          overrideNative: overrideNativeHls,
          useCueTags: true
        }
      },
      bigPlayButton: false,
      hotkeys: false,
      controlBar: {
        captionsButton: false,
        vpComponents: {
          vpVolumePanel: true,
          vpSubtitle: {
            play_uuid: this.uuid(),
            videoId: this.data().videoId(),
            show: this.showSubtitleSwitcher()
          },
          vpSeekBackwardButton: { show: this.data().isVideo() },
          vpSeekForwardButton: { show: this.data().isVideo() },
          vpNextButton: {
            show: (this.data().isVideo() && this.data().embed() === 'false'),
            videoId: this.data().videoId()
          },
          vpVidioLogo: {
            videoId: this.data().videoId(),
            streamType: this.data().streamType(),
            ignoreClick: this.ignoreClickVpVidioLogo(),
            show: (this.data().embed() == 'true' && this.data().hideLogo() == false)
          },
          progressControl: {
            seekBar: dvr_should_init || this.data().isVideo()
          },
          fullscreenToggle: {
            show: true
          },
          vpThumbnails: PlayerEnvironment.isDesktopSite()? this.data().thumbnails(): {},
          vpCinemaModeButton: {
            videoId: this.data().videoId(),
            show: (PlayerEnvironment.isDesktopSite() && this.data().embed() == "false" && this.data().isVideo())
          },
          vpRemainingTimeDisplay: {
            show: dvr_should_init || this.data().isVideo(),
            toggleable: this.data().isVideo()
          },
          vpLiveDisplay: !dvr_should_init,
          vpEpisodesListButton: this.data().seasonsList().length > 0,
          vpSettingsButton: {
            videoId: this.data().videoId(),
            play_uuid: this.uuid(),
            type: this.data().type(),
            resolutionMapping: this.data().resolutionMapping(),
            show: this.showSettingsButton()
          },
          vpMobileSettingsButton: {
            videoId: this.data().videoId(),
            play_uuid: this.uuid(),
            type: this.data().type(),
            resolutionMapping: this.data().resolutionMapping(),
            show: this.showMobileSettingsButton()
          }
        }
      },
      plugins: {
        loadingAnimation: {},
        bitrateSwitcher: {
          play_uuid: this.uuid(),
          type: this.data().type(),
          resolutionMapping: this.data().resolutionMapping()
        },
        initStartEvent: {
          data: this.data(),
        }
      }
    };
  }
}
